import { useState } from 'react';
import { Modal } from 'react-bootstrap';

import useAddDependency from '../../../../hooks/api/dependencies/useAddDependency';
import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import ButtonGroup from '../../../generic/Button/ButtonGroup';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import { icons } from '../../../generic/Icon/icons';
import DependencySummary from '../../../modals/DependencySummary/DependencySummary';

import { snippetConversionToDependency } from './utils';
import type { SnippetConversion } from './utils';

import styles from '../../../modals/DependencySummary/DependencySummary.module.less';

type Props = {
  userRevisionLocator: string;
  snippetConversion: SnippetConversion | null;
  setSnippetConversion: (conversion: SnippetConversion | null) => void;
};

/**
 * This modal enables users to convert a snippet to a vendored dependency.
 * The dependency locator is fixed; but the directory under which it is vendored can be edited by the user.
 *
 * @param {object} props - ModalSnippetConversion properties
 * @param {string} props.userRevisionLocator - the revision the new dependency will be added to.
 * @param {SnippetConversion | null} props.snippetConversion - snippet conversion data; when null modal will not be displayed
 * @param {(conversion: SnippetConversion | null) => void} props.setSnippetConversion - callback to set snippetConversion
 */
const ModalSnippetConversion = ({
  userRevisionLocator,
  snippetConversion,
  setSnippetConversion,
}: Props) => {
  const [vendoredPath, setVendoredPath] = useState<string | null>(null);
  const { mutateAsync: addDependency } = useAddDependency(userRevisionLocator);

  const shouldShow = snippetConversion !== null;

  const onHide = () => {
    setSnippetConversion(null);
  };

  const onConvert = async () => {
    if (snippetConversion) {
      const finalVendoredPath = (vendoredPath ?? snippetConversion.vendoredPath);

      await addDependency({
        kind: 'new',
        newDependency: snippetConversionToDependency(snippetConversion, finalVendoredPath),
      });
      setSnippetConversion(null);
    }
  };

  if (snippetConversion === null) {
    return null;
  }

  return (
    <Modal
      dialogClassName={styles.dependencySummaryModalDialog}
      onHide={onHide}
      animation
      show={shouldShow}>
      <Modal.Header closeButton>
        <Modal.Title>Convert to Vendored Dependency</Modal.Title>
        <div className={styles.dependencySummarySubtitle}>
          <p>
            This will add the following vendored dependency to your project.
          </p>
        </div>
      </Modal.Header>
      <div className={styles.dependencySummaryModalBody}>
        <Modal.Body>
          <div className={styles.dependencySummaryEditableFilePath}>
            <LabeledTextInput
              id="convert-snippet-file-path"
              size="small"
              label="File Path"
              value={vendoredPath ?? snippetConversion.vendoredPath}
              onChange={(value) => setVendoredPath(value)}
            />
            <p className={styles.dependencySummaryNotes}>
              The file path is pre-filled based on the snippet matches.
            </p>
          </div>
          <DependencySummary summary={snippetConversion} />
        </Modal.Body>
      </div>
      <div className={styles.dependencySummaryModalFooter}>
        <Modal.Footer>
          <ButtonGroup>
            <Button
              type={BUTTON_TYPE.SECONDARY}
              size={BUTTON_SIZE.MEDIUM}
              onClick={onHide}>
              Cancel
            </Button>
            <Button
              type={BUTTON_TYPE.PRIMARY}
              size={BUTTON_SIZE.MEDIUM}
              icon={icons.arrowRightFromArc}
              onClick={onConvert}>
              Convert
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default ModalSnippetConversion;
